import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import './ProcessFlow.css';

const ProcessFlow = ({
  steps,
  orientation = 'horizontal', // 'horizontal' | 'vertical'
  numbered = true,
  title,
  subtitle,
  backgroundColor,
  maxWidth = 'lg',
  stepColor = '#1B422E',
  arrowColor = '#FE9E0D'
}) => {
  const isHorizontal = orientation === 'horizontal';

  return (
    <Box
      className="process-flow-section"
      sx={{
        backgroundColor: backgroundColor || 'transparent',
        py: { xs: 5, md: 7 }
      }}
    >
      <Container maxWidth={maxWidth}>
        {/* Section Header */}
        {(title || subtitle) && (
          <Box
            className="process-flow-header"
            sx={{
              textAlign: 'center',
              mb: { xs: 4, md: 5 }
            }}
          >
            {subtitle && (
              <Typography
                sx={{
                  fontFamily: '"Reem Kufi", sans-serif',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontWeight: 600,
                  color: '#FE9E0D',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  mb: 1
                }}
              >
                {subtitle}
              </Typography>
            )}

            {title && (
              <Typography
                variant="h2"
                sx={{
                  fontFamily: '"Reem Kufi", sans-serif',
                  fontSize: {
                    xs: 'clamp(1.75rem, 4vw, 2.5rem)',
                    md: 'clamp(2rem, 4vw, 3rem)'
                  },
                  fontWeight: 700,
                  color: '#1B422E',
                  lineHeight: 1.3
                }}
              >
                {title}
              </Typography>
            )}
          </Box>
        )}

        {/* Process Steps */}
        <Grid
          container
          spacing={{ xs: 3, md: 4 }}
          direction={isHorizontal ? 'row' : 'column'}
          alignItems={isHorizontal ? 'stretch' : 'center'}
          sx={{
            flexDirection: { xs: 'column', md: isHorizontal ? 'row' : 'column' }
          }}
        >
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              {/* Step Card */}
              <Grid item xs={12} md={isHorizontal ? 12 / steps.length : 12}>
                <Box
                  className="process-step"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: { xs: 2.5, md: 3 },
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    boxShadow: 2,
                    height: '100%',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: 5,
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  {/* Number Badge */}
                  {numbered && (
                    <Box
                      className="step-number"
                      sx={{
                        width: { xs: 50, md: 60 },
                        height: { xs: 50, md: 60 },
                        borderRadius: '50%',
                        backgroundColor: stepColor,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        fontWeight: 700,
                        fontFamily: '"Reem Kufi", sans-serif',
                        mb: 2,
                        boxShadow: 3
                      }}
                    >
                      {index + 1}
                    </Box>
                  )}

                  {/* Icon */}
                  {step.icon && (
                    <Box
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {React.createElement(step.icon, {
                        sx: {
                          fontSize: { xs: 40, md: 50 },
                          color: stepColor
                        }
                      })}
                    </Box>
                  )}

                  {/* Title */}
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: '"Reem Kufi", sans-serif',
                      fontSize: { xs: '1.1rem', md: '1.35rem' },
                      fontWeight: 600,
                      color: '#1B422E',
                      mb: 1.5,
                      lineHeight: 1.3
                    }}
                  >
                    {step.title}
                  </Typography>

                  {/* Description */}
                  {step.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: '"Reem Kufi", sans-serif',
                        fontSize: { xs: '0.95rem', md: '1rem' },
                        color: '#505050',
                        lineHeight: 1.6
                      }}
                    >
                      {step.description}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Arrow Between Steps */}
              {index < steps.length - 1 && (
                <Grid
                  item
                  sx={{
                    // Hide arrow on mobile, show on desktop
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {isHorizontal ? (
                    <ArrowForwardIcon
                      sx={{
                        fontSize: { md: 40 },
                        color: arrowColor,
                        animation: 'pulse 2s infinite'
                      }}
                    />
                  ) : (
                    <ArrowDownwardIcon
                      sx={{
                        fontSize: { md: 40 },
                        color: arrowColor,
                        animation: 'pulse 2s infinite'
                      }}
                    />
                  )}
                </Grid>
              )}
            </React.Fragment>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ProcessFlow;
